/**
 * Smart Records Engine — Premium Client Profile Page
 * 3-column layout (Stripe / Notion / Attio inspired)
 * - LEFT  : avatar, identity, consents, quick stats
 * - CENTER: tabs (Overview, Timeline, Scheda Verticale, Documenti, Appuntamenti)
 * - RIGHT : AI Assistant (summary, alerts, suggestions)
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useLanguage } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, MessageSquare, Mail, Calendar, FileText, Sparkles,
  Loader2, Plus, Trash2, Download, Upload, RefreshCw, Save, Edit2,
  Tag as TagIcon, Activity, ShieldCheck, AlertTriangle, History, ClipboardList,
  TrendingUp, Clock, Users as UsersIcon, FileSignature, X, Check,
  StickyNote, Wand2, ChevronRight, Wallet, Heart, Package, ScanLine,
} from "lucide-react";
import ClientRecordEditor from "../components/smart-records/ClientRecordEditor";
import TimelineList from "../components/smart-records/TimelineList";
import DocumentsPanel from "../components/smart-records/DocumentsPanel";
import AIAssistantPanel from "../components/smart-records/AIAssistantPanel";
import SmartOdontogram from "../components/dental/SmartOdontogram";
import ConsentManager from "../components/dental/ConsentManager";
import PrescriptionsTab from "../components/dental/PrescriptionsTab";
import BillingTab from "../components/billing/BillingTab";
import SmartBodyMap from "../components/wellness/SmartBodyMap";
import RecoveryTab from "../components/wellness/RecoveryTab";
import SessionPackagesTab from "../components/wellness/SessionPackagesTab";
import PostureTab from "../components/wellness/PostureTab";
import ExercisesTab from "../components/wellness/ExercisesTab";
import PatientPortalCard from "../components/wellness/PatientPortalCard";
import { useIsDentalTenant } from "../hooks/useIsDentalTenant";
import { useIsWellnessTenant } from "../hooks/useIsWellnessTenant";

const TABS = [
  { key: "overview",    label_it: "Panoramica",       label_en: "Overview",      icon: Sparkles },
  { key: "timeline",    label_it: "Timeline",         label_en: "Timeline",      icon: History },
  { key: "record",      label_it: "Scheda verticale", label_en: "Vertical card", icon: ClipboardList },
  { key: "odontogram",  label_it: "Odontogramma",     label_en: "Odontogram",    icon: Activity, dentalOnly: true },
  { key: "prescriptions", label_it: "Ricette",        label_en: "Prescriptions", icon: ScanLine },
  { key: "body_map",    label_it: "Body Map",         label_en: "Body Map",      icon: Activity, wellnessOnly: true },
  { key: "recovery",    label_it: "Recovery",         label_en: "Recovery",      icon: Heart,    wellnessOnly: true },
  { key: "posture",     label_it: "Postura",          label_en: "Posture",       icon: FileText, wellnessOnly: true },
  { key: "exercises",   label_it: "Esercizi",         label_en: "Exercises",     icon: ClipboardList, wellnessOnly: true },
  { key: "packages",    label_it: "Pacchetti",        label_en: "Packages",      icon: Package,  wellnessOnly: true },
  { key: "billing",     label_it: "Pagamenti",        label_en: "Billing",       icon: Wallet, dentalOnly: true },
  { key: "appts",       label_it: "Appuntamenti",     label_en: "Appointments",  icon: Calendar },
  { key: "documents",   label_it: "Documenti",        label_en: "Documents",     icon: FileText },
];

const fmtCurrency = (n) => `€${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const ClientProfile = () => {
  const { clientId } = useParams();
  const { language } = useLanguage();
  const t = (it, en) => language === "it" ? it : en;
  const navigate = useNavigate();

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const isDental = useIsDentalTenant();
  const isWellness = useIsWellnessTenant();
  const visibleTabs = useMemo(
    () => TABS.filter(tb =>
      (!tb.dentalOnly   || isDental) &&
      (!tb.wellnessOnly || isWellness)
    ),
    [isDental, isWellness]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clients/${clientId}/profile-bundle`, { withCredentials: true });
      setBundle(r.data);
    } catch (err) {
      toast.error(t("Errore nel caricamento", "Loading error"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const client = bundle?.client;
  const fullName = useMemo(() => {
    if (!client) return "";
    if (client.first_name || client.last_name) return `${client.first_name || ""} ${client.last_name || ""}`.trim();
    return client.name || t("Cliente", "Client");
  }, [client, language]);

  const initials = useMemo(() => {
    return (fullName || "C").trim().split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  }, [fullName]);

  const startEdit = () => {
    setEditForm({
      first_name: client.first_name || (client.name?.split(" ")[0] || ""),
      last_name:  client.last_name  || (client.name?.split(" ").slice(1).join(" ") || ""),
      email:      client.email || "",
      phone:      client.phone || "",
      birth_date: client.birth_date || "",
      gender:     client.gender || "",
      address:    client.address || "",
      city:       client.city || "",
      fiscal_code: client.fiscal_code || "",
      tags: (client.tags || []).join(", "),
      marketing_consent: !!client.marketing_consent,
      privacy_consent:   !!client.privacy_consent,
      whatsapp_optin:    !!client.whatsapp_optin,
      notes: client.notes || "",
    });
    setEditing(true);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { ...editForm };
      payload.tags = (editForm.tags || "").split(",").map(s => s.trim()).filter(Boolean);
      payload.name = `${payload.first_name || ""} ${payload.last_name || ""}`.trim() || client.name;
      await axios.patch(`${API}/clients/${clientId}`, payload, { withCredentials: true });
      toast.success(t("Profilo salvato", "Profile saved"));
      setEditing(false);
      load();
    } catch {
      toast.error(t("Errore salvataggio", "Save error"));
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading || !bundle) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      </AdminLayout>
    );
  }

  const totalSpent = client.total_spent || client.total_revenue || 0;
  const totalAppts = (bundle.appointments || []).length || client.total_appointments || client.total_visits || 0;
  const lastAppt = (bundle.appointments || []).find(a => ["completed", "confirmed", "done"].includes(a.status))
                || (bundle.appointments || [])[0];
  const upcomingAppt = (bundle.appointments || []).find(a =>
    new Date(a.date) > new Date() && ["pending", "confirmed", "awaiting_confirmation"].includes(a.status)
  );

  return (
    <AdminLayout>
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-4 space-y-4" data-testid="client-profile-page">
        {/* ========== TOP BAR ========== */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate("/clients")}
            className="rounded-full text-muted-foreground hover:text-foreground"
            data-testid="client-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> {t("Torna ai clienti", "Back to clients")}
          </Button>
          <div className="flex items-center gap-2">
            {client.phone && (
              <Button variant="outline" size="sm" className="rounded-full"
                onClick={() => window.open(`tel:${client.phone}`)} data-testid="client-call-btn">
                <Phone className="w-4 h-4" />
              </Button>
            )}
            {client.phone && (
              <Button variant="outline" size="sm" className="rounded-full"
                onClick={() => window.open(`https://wa.me/${client.phone.replace(/[^\d+]/g, "")}`)} data-testid="client-wa-btn">
                <MessageSquare className="w-4 h-4" />
              </Button>
            )}
            {client.email && (
              <Button variant="outline" size="sm" className="rounded-full"
                onClick={() => window.open(`mailto:${client.email}`)} data-testid="client-email-btn">
                <Mail className="w-4 h-4" />
              </Button>
            )}
            <Link to={`/calendar`}>
              <Button size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="client-new-appt-btn">
                <Plus className="w-4 h-4 mr-1.5" /> {t("Nuovo appuntamento", "New appointment")}
              </Button>
            </Link>
          </div>
        </div>

        {/* ========== HERO HEADER ========== */}
        <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-violet-500/5 via-transparent to-cyan-500/5 dark:from-violet-500/10 dark:to-cyan-500/10 p-5 sm:p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-violet-500 via-purple-600 to-cyan-500 text-white flex items-center justify-center text-2xl font-bold shadow-lg flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white truncate" data-testid="client-name">{fullName}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1.5 text-sm text-muted-foreground">
                {client.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{client.phone}</span>}
                {client.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{client.email}</span>}
                {client.city && <span>📍 {client.city}</span>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(client.tags || []).map(tg => (
                  <span key={tg} className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">
                    {tg}
                  </span>
                ))}
                {bundle.template && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 inline-flex items-center gap-1">
                    <ClipboardList className="w-3 h-3" /> {bundle.template.name}
                  </span>
                )}
              </div>
            </div>
            {/* Hero stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 ml-auto" data-testid="client-hero-stats">
              <Stat label={t("Totale speso", "Total spent")} value={fmtCurrency(totalSpent)} icon={TrendingUp} />
              <Stat label={t("Appuntamenti", "Appointments")} value={totalAppts} icon={Calendar} />
              <Stat label={t("Ultima visita", "Last visit")} value={fmtDate(lastAppt?.date || client.last_visit)} icon={Clock} />
              <Stat label={t("Prossima", "Upcoming")} value={fmtDate(upcomingAppt?.date || client.next_visit_at)} icon={Activity} />
            </div>
          </div>
        </div>

        {/* ========== 3-COL LAYOUT ========== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* LEFT */}
          <aside className="lg:col-span-3 space-y-3" data-testid="client-left-sidebar">
            <Card title={t("Anagrafica", "Profile")} action={
              !editing ? (
                <Button size="sm" variant="ghost" onClick={startEdit} className="rounded-full h-7" data-testid="client-edit-btn">
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
              ) : null
            }>
              {!editing ? (
                <ProfileFields client={client} t={t} />
              ) : (
                <ProfileEditForm
                  form={editForm} setForm={setEditForm}
                  onSave={saveProfile} onCancel={() => setEditing(false)}
                  saving={savingProfile} t={t}
                />
              )}
            </Card>

            <Card title={t("Consensi GDPR", "GDPR Consents")}>
              <div className="space-y-2 text-xs">
                <ConsentRow label={t("Privacy Policy", "Privacy")}     active={!!client.privacy_consent} />
                <ConsentRow label={t("Marketing", "Marketing")}        active={!!client.marketing_consent} />
                <ConsentRow label={t("WhatsApp / SMS", "WhatsApp/SMS")} active={!!client.whatsapp_optin} />
              </div>
            </Card>

            {isDental && (
              <Card title={t("Firma digitale", "Digital signatures")}>
                <ConsentManager clientId={clientId} />
              </Card>
            )}

            {isWellness && (
              <Card title={t("Consensi Wellness", "Wellness consents")}>
                <ConsentManager clientId={clientId} apiBase="/wellness/consents" />
              </Card>
            )}

            {isWellness && (
              <Card title={t("Patient Portal", "Patient Portal")}>
                <PatientPortalCard clientId={clientId} clientName={fullName} t={t} />
              </Card>
            )}

            <Card title={t("Note rapide", "Quick notes")}>
              <NoteQuickAdd clientId={clientId} onAdded={load} t={t} />
            </Card>
          </aside>

          {/* CENTER */}
          <main className="lg:col-span-6">
            {/* TABS */}
            <div className="flex gap-1 p-1 rounded-2xl border border-slate-200/60 dark:border-white/10 bg-slate-50/80 dark:bg-slate-900/40 backdrop-blur overflow-x-auto" data-testid="client-tabs">
              {visibleTabs.map(tb => {
                const Icon = tb.icon;
                const active = tab === tb.key;
                return (
                  <button
                    key={tb.key}
                    onClick={() => setTab(tb.key)}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                      active
                        ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`client-tab-${tb.key}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {t(tb.label_it, tb.label_en)}
                  </button>
                );
              })}
            </div>

            <div className="mt-4">
              {tab === "overview" && (
                <OverviewTab bundle={bundle} t={t} onJumpTab={setTab} />
              )}
              {tab === "timeline" && (
                <TimelineList clientId={clientId} initial={bundle.timeline} onChange={load} t={t} />
              )}
              {tab === "record" && (
                bundle.template
                  ? <ClientRecordEditor
                      clientId={clientId}
                      template={bundle.template}
                      record={bundle.record}
                      onSaved={load}
                      t={t}
                    />
                  : <EmptyState
                      title={t("Nessuna scheda verticale per questo settore", "No vertical card for this sector")}
                      description={t("Phase 1 supporta lo studio dentistico. Altri settori in arrivo nelle prossime release.",
                                      "Phase 1 supports dental studios. Other sectors coming in upcoming releases.")}
                    />
              )}
              {tab === "appts" && (
                <AppointmentsTab list={bundle.appointments} t={t} />
              )}
              {tab === "odontogram" && isDental && (
                <SmartOdontogram clientId={clientId} t={t} />
              )}
              {tab === "prescriptions" && (
                <PrescriptionsTab clientId={clientId} />
              )}
              {tab === "body_map" && isWellness && (
                <SmartBodyMap clientId={clientId} clientName={fullName} />
              )}
              {tab === "recovery" && isWellness && (
                <RecoveryTab clientId={clientId} />
              )}
              {tab === "posture" && isWellness && (
                <PostureTab clientId={clientId} />
              )}
              {tab === "exercises" && isWellness && (
                <ExercisesTab clientId={clientId} />
              )}
              {tab === "packages" && isWellness && (
                <SessionPackagesTab clientId={clientId} />
              )}
              {tab === "billing" && isDental && (
                <BillingTab clientId={clientId} clientName={fullName} />
              )}
              {tab === "documents" && (
                <DocumentsPanel
                  clientId={clientId}
                  initial={bundle.documents}
                  onChange={load}
                  t={t}
                />
              )}
            </div>
          </main>

          {/* RIGHT */}
          <aside className="lg:col-span-3" data-testid="client-right-sidebar">
            <AIAssistantPanel clientId={clientId} initial={bundle.ai} onRefreshed={load} t={t} />
          </aside>
        </div>
      </div>
    </AdminLayout>
  );
};

/* ============================================================
 * SUB COMPONENTS
 * ============================================================ */
const Card = ({ title, action, children }) => (
  <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/60 p-3.5">
    <div className="flex items-center justify-between mb-2.5">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Stat = ({ label, value, icon: Icon }) => (
  <div className="rounded-2xl bg-white/60 dark:bg-white/5 backdrop-blur border border-slate-200/60 dark:border-white/10 px-3 py-2 min-w-[120px]">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="font-black text-slate-900 dark:text-white text-sm mt-0.5 truncate">{value}</div>
  </div>
);

const ProfileFields = ({ client, t }) => (
  <dl className="space-y-1.5 text-xs">
    <Field label={t("Nascita", "Birth")} value={client.birth_date} />
    <Field label={t("Sesso", "Gender")} value={client.gender} />
    <Field label={t("Indirizzo", "Address")} value={client.address} />
    <Field label={t("Città", "City")} value={client.city} />
    <Field label={t("Codice fiscale", "Fiscal code")} value={client.fiscal_code} />
    <Field label={t("Operatore assegnato", "Assigned operator")} value={client.assigned_operator} />
    <Field label={t("Origine", "Source")} value={client.source} />
  </dl>
);

const Field = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-slate-100 dark:border-white/5 last:border-0">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="font-medium text-right text-slate-800 dark:text-slate-200 truncate max-w-[60%]">{value || "—"}</dd>
  </div>
);

const ProfileEditForm = ({ form, setForm, onSave, onCancel, saving, t }) => (
  <div className="space-y-2 text-xs">
    <div className="grid grid-cols-2 gap-2">
      <Input className="text-xs h-8" placeholder={t("Nome", "First name")} value={form.first_name || ""}
             onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="client-edit-firstname" />
      <Input className="text-xs h-8" placeholder={t("Cognome", "Last name")} value={form.last_name || ""}
             onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="client-edit-lastname" />
    </div>
    <Input className="text-xs h-8" type="email" placeholder="email" value={form.email || ""}
           onChange={e => setForm({ ...form, email: e.target.value })} />
    <Input className="text-xs h-8" placeholder={t("Telefono", "Phone")} value={form.phone || ""}
           onChange={e => setForm({ ...form, phone: e.target.value })} />
    <div className="grid grid-cols-2 gap-2">
      <Input className="text-xs h-8" type="date" value={form.birth_date || ""}
             onChange={e => setForm({ ...form, birth_date: e.target.value })} />
      <Select value={form.gender || ""} onValueChange={v => setForm({ ...form, gender: v })}>
        <SelectTrigger className="text-xs h-8"><SelectValue placeholder={t("Sesso", "Gender")} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="M">M</SelectItem>
          <SelectItem value="F">F</SelectItem>
          <SelectItem value="X">{t("Altro", "Other")}</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <Input className="text-xs h-8" placeholder={t("Indirizzo", "Address")} value={form.address || ""}
           onChange={e => setForm({ ...form, address: e.target.value })} />
    <div className="grid grid-cols-2 gap-2">
      <Input className="text-xs h-8" placeholder={t("Città", "City")} value={form.city || ""}
             onChange={e => setForm({ ...form, city: e.target.value })} />
      <Input className="text-xs h-8" placeholder={t("Cod. fiscale", "Fiscal code")} value={form.fiscal_code || ""}
             onChange={e => setForm({ ...form, fiscal_code: e.target.value })} />
    </div>
    <Input className="text-xs h-8" placeholder={t("Tag (separati da virgola)", "Tags (comma-separated)")} value={form.tags || ""}
           onChange={e => setForm({ ...form, tags: e.target.value })} />

    <div className="grid grid-cols-1 gap-1.5 pt-1.5">
      <ConsentToggle label={t("Privacy", "Privacy")}        v={form.privacy_consent}   onChange={v => setForm({ ...form, privacy_consent: v })} />
      <ConsentToggle label={t("Marketing", "Marketing")}    v={form.marketing_consent} onChange={v => setForm({ ...form, marketing_consent: v })} />
      <ConsentToggle label={t("WhatsApp / SMS", "WhatsApp/SMS")} v={form.whatsapp_optin}    onChange={v => setForm({ ...form, whatsapp_optin: v })} />
    </div>

    <Textarea className="text-xs min-h-[60px]" placeholder={t("Note...", "Notes...")} value={form.notes || ""}
              onChange={e => setForm({ ...form, notes: e.target.value })} />

    <div className="flex gap-1.5 pt-1">
      <Button size="sm" className="rounded-full flex-1 h-8" onClick={onSave} disabled={saving} data-testid="client-save-btn">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> {t("Salva", "Save")}</>}
      </Button>
      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={onCancel}>
        {t("Annulla", "Cancel")}
      </Button>
    </div>
  </div>
);

const ConsentToggle = ({ label, v, onChange }) => (
  <div className="flex items-center justify-between text-[11px] py-0.5">
    <span className="text-slate-600 dark:text-slate-300">{label}</span>
    <Switch checked={!!v} onCheckedChange={onChange} />
  </div>
);

const ConsentRow = ({ label, active }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-600 dark:text-slate-300">{label}</span>
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
      active
        ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
        : "bg-slate-100 dark:bg-slate-800 text-slate-500"
    }`}>
      {active ? <><Check className="w-2.5 h-2.5" /> ON</> : <><X className="w-2.5 h-2.5" /> OFF</>}
    </span>
  </div>
);

const NoteQuickAdd = ({ clientId, onAdded, t }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/timeline`, {
        event_type: "note_added",
        title: t("Nota", "Note"),
        description: text.trim(),
      }, { withCredentials: true });
      setText("");
      onAdded();
      toast.success(t("Nota aggiunta", "Note added"));
    } catch {
      toast.error(t("Errore", "Error"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <Textarea
        value={text} onChange={e => setText(e.target.value)}
        placeholder={t("Scrivi una nota rapida...", "Write a quick note...")}
        className="text-xs min-h-[55px]"
        data-testid="client-note-input"
      />
      <Button size="sm" className="w-full h-8 rounded-full" onClick={submit} disabled={saving || !text.trim()} data-testid="client-note-add-btn">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
          <><StickyNote className="w-3 h-3 mr-1" /> {t("Aggiungi", "Add")}</>
        )}
      </Button>
    </div>
  );
};

/* ============================================================
 * TABS — Overview / Appointments
 * ============================================================ */
const OverviewTab = ({ bundle, t, onJumpTab }) => {
  const recentTimeline = (bundle.timeline || []).slice(0, 5);
  const ai = bundle.ai;
  return (
    <div className="space-y-4">
      {ai?.summary && (
        <div className="rounded-2xl border border-violet-200 dark:border-violet-900/40 bg-gradient-to-br from-violet-50 to-cyan-50 dark:from-violet-950/30 dark:to-cyan-950/30 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300 mb-1">
                {t("Riassunto AI", "AI Summary")}
              </p>
              <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{ai.summary}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ShortcutCard
          icon={History}
          title={t("Eventi recenti", "Recent events")}
          count={(bundle.timeline || []).length}
          onClick={() => onJumpTab("timeline")}
        />
        <ShortcutCard
          icon={ClipboardList}
          title={t("Scheda verticale", "Vertical card")}
          count={bundle.record ? "✓" : "—"}
          onClick={() => onJumpTab("record")}
        />
        <ShortcutCard
          icon={Calendar}
          title={t("Appuntamenti", "Appointments")}
          count={(bundle.appointments || []).length}
          onClick={() => onJumpTab("appts")}
        />
        <ShortcutCard
          icon={FileText}
          title={t("Documenti", "Documents")}
          count={(bundle.documents || []).length}
          onClick={() => onJumpTab("documents")}
        />
      </div>

      {recentTimeline.length > 0 && (
        <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold">{t("Ultimi eventi", "Latest events")}</h3>
            <button onClick={() => onJumpTab("timeline")} className="text-xs text-violet-600 dark:text-violet-300 hover:underline inline-flex items-center gap-0.5">
              {t("Vedi timeline", "See timeline")} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <ol className="space-y-2.5">
            {recentTimeline.map(e => (
              <li key={e.event_id} className="flex gap-3 text-xs">
                <div className="w-1.5 rounded-full bg-violet-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 dark:text-white truncate">{e.title}</p>
                  {e.description && <p className="text-muted-foreground text-[11px] line-clamp-2">{e.description}</p>}
                  <p className="text-[10px] text-slate-400 mt-0.5">{fmtDate(e.created_at)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

const ShortcutCard = ({ icon: Icon, title, count, onClick }) => (
  <button
    onClick={onClick}
    className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/60 p-3.5 text-left hover:border-violet-400 transition-colors"
  >
    <div className="flex items-center justify-between">
      <div className="w-9 h-9 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
        <Icon className="w-4 h-4 text-violet-600 dark:text-violet-300" />
      </div>
      <span className="text-2xl font-black text-slate-900 dark:text-white">{count}</span>
    </div>
    <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mt-2">{title}</p>
  </button>
);

const AppointmentsTab = ({ list, t }) => {
  if (!list || list.length === 0) {
    return <EmptyState
      title={t("Nessun appuntamento", "No appointments")}
      description={t("Quando il cliente prenoterà comparirà qui.", "Once the client books, it'll appear here.")}
    />;
  }
  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 overflow-hidden" data-testid="client-appts-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-900/40 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-3 py-2">{t("Data", "Date")}</th>
            <th className="text-left px-3 py-2">{t("Servizio", "Service")}</th>
            <th className="text-left px-3 py-2 hidden sm:table-cell">{t("Operatore", "Staff")}</th>
            <th className="text-left px-3 py-2">{t("Stato", "Status")}</th>
            <th className="text-right px-3 py-2">€</th>
          </tr>
        </thead>
        <tbody>
          {list.map(a => (
            <tr key={a.appointment_id || a.id || a.date} className="border-t border-slate-100 dark:border-white/5">
              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(a.date)} {a.time && <span className="text-muted-foreground">· {a.time}</span>}</td>
              <td className="px-3 py-2">{a.service_name || a.service || "—"}</td>
              <td className="px-3 py-2 hidden sm:table-cell text-muted-foreground">{a.staff_name || "—"}</td>
              <td className="px-3 py-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  a.status === "completed" || a.status === "confirmed" || a.status === "done"
                    ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                    : a.status === "cancelled" ? "bg-red-100 dark:bg-red-900/40 text-red-600"
                    : "bg-amber-100 dark:bg-amber-900/40 text-amber-700"
                }`}>
                  {a.status}
                </span>
              </td>
              <td className="px-3 py-2 text-right font-mono">{a.price ? fmtCurrency(a.price) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EmptyState = ({ title, description, icon: Icon = AlertTriangle }) => (
  <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center" data-testid="client-empty-state">
    <Icon className="w-8 h-8 mx-auto text-slate-400" />
    <p className="font-semibold text-sm text-slate-700 dark:text-slate-200 mt-3">{title}</p>
    {description && <p className="text-xs text-muted-foreground mt-1.5 max-w-sm mx-auto">{description}</p>}
  </div>
);

export default ClientProfile;
